import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useClient } from '@/contexts/ClientContext';
import { BrandTile } from '@/components/BrandTile';

import { triggerConfetti } from '@/lib/confetti';

interface TeasingIntroProps {
  onDone: () => void;
}

// Reduced particles for phase 3 (8 instead of 12)
const PARTICLES = Array.from({ length: 8 }, (_, i) => {
  const angle = (i / 8) * Math.PI * 2;
  const dist = 60 + Math.random() * 60;
  return {
    id: i,
    x: Math.cos(angle) * dist,
    y: Math.sin(angle) * dist,
    size: 4 + Math.random() * 6,
    delay: i * 0.05,
    color: i % 3,
  };
});

// SVG wheel segment paths for a 6-segment wheel
const WHEEL_SEGMENTS = Array.from({ length: 6 }, (_, i) => {
  const startAngle = (i / 6) * 2 * Math.PI - Math.PI / 2;
  const endAngle = ((i + 1) / 6) * 2 * Math.PI - Math.PI / 2;
  const r = 60;
  const x1 = r * Math.cos(startAngle);
  const y1 = r * Math.sin(startAngle);
  const x2 = r * Math.cos(endAngle);
  const y2 = r * Math.sin(endAngle);
  return `M0,0 L${x1},${y1} A${r},${r} 0 0,1 ${x2},${y2} Z`;
});

const TeasingIntro = ({ onDone }: TeasingIntroProps) => {
  const { client, clientSettings } = useClient();

  const [phase, setPhase] = useState(1);
  const [exiting, setExiting] = useState(false);
  const confettiFiredRef = useRef(false);

  const primary = clientSettings?.brand_primary_color ?? '#00ff88';
  const secondary = clientSettings?.brand_secondary_color ?? '#00d4ff';
  const dark = '#0d0d1a';

  const segmentColors = [primary, secondary, dark, primary, secondary, dark];

  const skip = useCallback(() => {
    onDone();
  }, [onDone]);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(2), 1200);
    const t2 = setTimeout(() => setPhase(3), 2400);
    const t3 = setTimeout(() => setExiting(true), 3600);
    const t4 = setTimeout(() => {
      onDone();
    }, 4200);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire confetti when phase 3 starts
  useEffect(() => {
    if (phase === 3 && !confettiFiredRef.current) {
      confettiFiredRef.current = true;
      setTimeout(() => triggerConfetti('stars'), 400);
    }
  }, [phase]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center"
      style={{ background: 'rgba(5, 5, 15, 0.97)' }}
      animate={{ opacity: exiting ? 0 : 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      {/* The venue leads from second zero */}
      <motion.div
        className="absolute inset-x-0 top-6 flex flex-col items-center gap-2 px-16 pointer-events-none"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: 'easeOut' }}
      >
        <BrandTile size="md" decorative />
        {client?.name && (
          <p className="max-w-full truncate text-sm font-semibold text-white/85">{client.name}</p>
        )}
      </motion.div>

      {/* Skip button */}
      <button
        onClick={skip}
        className="absolute top-4 right-4 text-sm px-3 py-1.5 rounded-full border transition-opacity opacity-50 hover:opacity-100"
        style={{ borderColor: `${primary}44`, color: primary }}
        aria-label="Passer l'intro"
      >
        Passer →
      </button>

      {/* Phase content */}
      <div className="relative flex flex-col items-center justify-center gap-6 min-h-[260px]">
        <AnimatePresence mode="wait">
          {phase === 1 && (
            <motion.div
              key="phase1"
              className="flex flex-col items-center gap-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{ duration: 0.3 }}
            >
              {/* NFC arcs + Smartphone */}
              <div className="relative flex items-center justify-center w-40 h-40">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute rounded-full border-2"
                    style={{ borderColor: primary }}
                    initial={{ scale: 0.4, opacity: 0.8, width: 80, height: 80 }}
                    animate={{ scale: 3, opacity: 0 }}
                    transition={{
                      duration: 1.0,
                      delay: i * 0.2,
                      repeat: Infinity,
                      ease: 'easeOut',
                    }}
                  />
                ))}

                <div className="relative z-10">
                  <svg width="64" height="96" viewBox="0 0 64 96" fill="none">
                    <rect x="4" y="2" width="56" height="92" rx="8" stroke={primary} strokeWidth="3" fill={`${primary}15`} />
                    <rect x="18" y="6" width="28" height="4" rx="2" fill={primary} opacity="0.5" />
                    <rect x="14" y="16" width="36" height="52" rx="4" fill={`${secondary}20`} stroke={secondary} strokeWidth="1" />
                    <circle cx="32" cy="84" r="4" fill={primary} opacity="0.6" />
                    <text x="32" y="50" textAnchor="middle" fontSize="20" fill={primary}>📶</text>
                    <motion.rect
                      x="14" y="0" width="36" height="2" rx="1"
                      fill={primary}
                      style={{ filter: `drop-shadow(0 0 4px ${primary})` }}
                      initial={{ y: 16, opacity: 1 }}
                      animate={{ y: 68, opacity: [1, 0.6, 1] }}
                      transition={{ repeat: Infinity, duration: 0.7, ease: 'linear' }}
                    />
                  </svg>
                </div>
              </div>

              <motion.p
                className="text-2xl font-bold tracking-widest uppercase"
                style={{ color: primary, textShadow: `0 0 20px ${primary}` }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                Scannez...
              </motion.p>
            </motion.div>
          )}

          {phase === 2 && (
            <motion.div
              key="phase2"
              className="flex flex-col items-center gap-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{ duration: 0.3 }}
            >
              <div
                className="relative flex items-center justify-center"
                style={{ filter: `drop-shadow(0 0 16px ${primary}80)` }}
              >
                <div
                  className="absolute -top-3 z-20"
                  style={{
                    width: 0, height: 0,
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: `16px solid ${primary}`,
                    filter: `drop-shadow(0 0 4px ${primary})`,
                  }}
                />
                <motion.svg
                  width="140" height="140"
                  viewBox="-70 -70 140 140"
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 1440 }}
                  transition={{ duration: 1.0, ease: 'easeOut' }}
                >
                  {WHEEL_SEGMENTS.map((d, i) => (
                    <path
                      key={i}
                      d={d}
                      fill={segmentColors[i]}
                      stroke="#0d0d1a"
                      strokeWidth="2"
                      opacity={segmentColors[i] === dark ? 0.5 : 1}
                    />
                  ))}
                  <circle cx="0" cy="0" r="10" fill="#0d0d1a" stroke={primary} strokeWidth="2" />
                </motion.svg>
              </div>

              <motion.p
                className="text-2xl font-bold tracking-widest uppercase"
                style={{ color: secondary, textShadow: `0 0 20px ${secondary}` }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                Jouez...
              </motion.p>
            </motion.div>
          )}

          {phase === 3 && (
            <motion.div
              key="phase3"
              className="flex flex-col items-center gap-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Gift box with CSS particles */}
              <div className="relative flex items-center justify-center w-40 h-40">
                {/* Particles - pure CSS animation */}
                {PARTICLES.map((p) => (
                  <div
                    key={p.id}
                    className="absolute rounded-full teasing-particle"
                    style={{
                      width: p.size,
                      height: p.size,
                      background: p.color === 0 ? primary : p.color === 1 ? secondary : '#ffffff',
                      boxShadow: `0 0 6px ${p.color === 0 ? primary : secondary}`,
                      '--px': `${p.x}px`,
                      '--py': `${p.y}px`,
                      animationDelay: `${p.delay}s`,
                    } as React.CSSProperties}
                  />
                ))}

                {/* Gift box SVG */}
                <div className="relative z-10" style={{ perspective: '200px' }}>
                  <svg width="100" height="100" viewBox="0 0 100 100">
                    <rect x="10" y="50" width="80" height="45" rx="3" fill={`${primary}20`} stroke={primary} strokeWidth="2.5" />
                    <line x1="10" y1="50" x2="20" y2="50" stroke={secondary} strokeWidth="2" />
                    <line x1="80" y1="50" x2="90" y2="50" stroke={secondary} strokeWidth="2" />
                    <line x1="10" y1="95" x2="20" y2="95" stroke={secondary} strokeWidth="2" />
                    <line x1="80" y1="95" x2="90" y2="95" stroke={secondary} strokeWidth="2" />
                    <rect x="43" y="50" width="14" height="45" fill={`${secondary}40`} />
                    <rect x="10" y="66" width="80" height="10" fill={`${secondary}30`} />
                  </svg>

                  {/* Lid - keep motion for the open effect */}
                  <motion.div
                    className="absolute left-0 w-full"
                    style={{ top: 0, transformOrigin: 'top center', transformStyle: 'preserve-3d' }}
                    initial={{ rotateX: 0, y: 0 }}
                    animate={{ rotateX: -85, y: -24 }}
                    transition={{ delay: 0.1, duration: 0.45, ease: 'easeOut' }}
                  >
                    <svg width="100" height="30" viewBox="0 0 100 30">
                      <rect x="8" y="14" width="84" height="16" rx="3" fill={`${primary}30`} stroke={primary} strokeWidth="2.5" />
                      <line x1="8" y1="14" x2="20" y2="14" stroke={secondary} strokeWidth="2" />
                      <line x1="80" y1="14" x2="92" y2="14" stroke={secondary} strokeWidth="2" />
                      <ellipse cx="34" cy="10" rx="18" ry="9" fill={`${secondary}50`} stroke={secondary} strokeWidth="1.5" />
                      <ellipse cx="66" cy="10" rx="18" ry="9" fill={`${secondary}50`} stroke={secondary} strokeWidth="1.5" />
                      <circle cx="50" cy="10" r="5" fill={secondary} />
                    </svg>
                  </motion.div>
                </div>
              </div>

              <motion.p
                className="text-3xl font-bold tracking-widest uppercase"
                style={{
                  background: `linear-gradient(135deg, ${primary}, ${secondary})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: `drop-shadow(0 0 12px ${primary}80)`,
                }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                Gagnez !
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Phase dots indicator */}
      <div className="absolute bottom-8 flex gap-2">
        {[1, 2, 3].map((p) => (
          <div
            key={p}
            className="rounded-full transition-all duration-300"
            style={{
              background: p === phase ? primary : `${primary}30`,
              boxShadow: p === phase ? `0 0 8px ${primary}` : 'none',
              width: p === phase ? 24 : 8,
              height: 8,
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default TeasingIntro;
